import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import * as api from '@ubeya/shared/services/api';
import { ImagesCarousel } from '@ubeya/shared-web/components';
import { MAX_VISIBLE_IMAGES, isVideoFile } from '../../helpDeskUtils';
import VideoPreviewThumb from '../VideoPreviewThumb';

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $isSingle }) => ($isSingle ? '1fr' : '1fr 1fr')};
  gap: 4px;
  overflow: hidden;
  width: 350px;
  max-width: 100%;
  margin-bottom: 4px;
`;

const ImageThumb = styled.img`
  width: 100%;
  aspect-ratio: ${({ $isWide }) => ($isWide ? '2 / 1' : 1)};
  object-fit: cover;
  cursor: pointer;
  display: block;
  border-radius: 6px;
  grid-column: ${({ $isWide }) => ($isWide ? '1 / -1' : 'auto')};
`;

const MediaOverlayThumb = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $isWide }) => ($isWide ? '2 / 1' : 1)};
  grid-column: ${({ $isWide }) => ($isWide ? '1 / -1' : 'auto')};
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;

  ${ImageThumb} {
    cursor: inherit;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => `${theme.colors.black}80`};
  }
`;

const RemainingCount = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: 32px;
  font-weight: 500;
  z-index: 1;
`;

const ImageOverlay = styled.div`
  width: 100%;
  aspect-ratio: ${({ $isWide }) => ($isWide ? '2 / 1' : 1)};
  grid-column: ${({ $isWide }) => ($isWide ? '1 / -1' : 'auto')};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => `${theme.colors.black}80`};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ $compact }) => ($compact ? '16px' : '18px')};
  font-weight: 700;
  cursor: pointer;
`;

const ImageAttachments = ({ media, accountId, ticketId, compact = false }) => {
  const [urls, setUrls] = useState({});
  const [carouselIndex, setCarouselIndex] = useState();

  useEffect(() => {
    let cancelled = false;
    media.forEach(async (item) => {
      try {
        const res = await api.getHelpDeskAttachmentUrl({ accountId, ticketId, attachmentId: item.id });
        const url = res?.data?.url || res?.url;
        if (!cancelled && url) {
          setUrls((prev) => ({ ...prev, [item.id]: url }));
        }
      } catch {
        // silent
      }
    });
    return () => {
      cancelled = true;
    };
  }, [media, accountId, ticketId]);

  const carouselViews = useMemo(
    () =>
      media
        .filter((item) => urls[item.id])
        .map((item) =>
          isVideoFile(item)
            ? { id: item.id, source: urls[item.id], videoUrl: true }
            : { id: item.id, source: urls[item.id] }
        ),
    [media, urls]
  );

  const handleMediaClick = useCallback(
    (gridIdx) => {
      const item = media[gridIdx];
      if (!urls[item?.id]) return;
      const viewIdx = carouselViews.findIndex((v) => v.id === item.id);
      if (viewIdx >= 0) setCarouselIndex(viewIdx);
    },
    [media, urls, carouselViews]
  );

  const visible = media.slice(0, MAX_VISIBLE_IMAGES);
  const remaining = media.length - MAX_VISIBLE_IMAGES;

  return (
    <>
      <ImageGrid $compact={compact} $isSingle={media.length === 1}>
        {visible.map((item, idx) => {
          const url = urls[item.id];
          const isWide = media.length === 3 && idx === 2;
          const isVideo = isVideoFile(item);
          const alt = item.fileName || item.name || '';

          if (idx === MAX_VISIBLE_IMAGES - 1 && remaining > 0) {
            return url ? (
              <MediaOverlayThumb key={item.id} onClick={() => handleMediaClick(idx)} $isWide={isWide}>
                {isVideo ? (
                  <VideoPreviewThumb src={url} alt={alt} fill />
                ) : (
                  <ImageThumb src={url} alt={alt} $isWide={isWide} />
                )}
                <RemainingCount $compact={compact}>+{remaining}</RemainingCount>
              </MediaOverlayThumb>
            ) : (
              <ImageOverlay key={item.id} $compact={compact} $isWide={isWide}>
                &hellip;
              </ImageOverlay>
            );
          }
          if (!url) {
            return (
              <ImageOverlay key={item.id} $compact={compact} $isWide={isWide}>
                &hellip;
              </ImageOverlay>
            );
          }
          return isVideo ? (
            <VideoPreviewThumb
              key={item.id}
              src={url}
              alt={alt}
              fill
              withPlayButton
              isWide={isWide}
              onClick={() => handleMediaClick(idx)}
            />
          ) : (
            <ImageThumb key={item.id} src={url} alt={alt} onClick={() => handleMediaClick(idx)} $isWide={isWide} />
          );
        })}
      </ImageGrid>
      <ImagesCarousel views={carouselViews} currentIndex={carouselIndex} onClose={() => setCarouselIndex()} />
    </>
  );
};

export default ImageAttachments;
